import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarOff, CheckCheck, CheckSquare, Clock3, FileText, Phone, Trash2, UserCheck, Users, Wallet } from "lucide-react";

import api from "../api/api";
import { CACHE_TTLS, POLL_INTERVALS } from "../config/polling";
import useVisibilityPolling from "../hooks/useVisibilityPolling";
import { getOrFetchCached, isAbortError, runTrackedRequest, setCachedValue } from "../utils/cache";
import { CACHE_KEYS } from "../utils/cacheKeys";
import { getNotificationDestination } from "../utils/notificationNavigation";

function cleanMsg(str) {
  return (str || "").replace(/^[?\s]+/, "").trim();
}

const TYPE_META = {
  task: { Icon: CheckSquare, dot: "bg-blue-500", bg: "bg-blue-500/10", label: "Task" },
  expense: { Icon: Wallet, dot: "bg-emerald-500", bg: "bg-emerald-500/10", label: "Expense" },
  service_call: { Icon: Phone, dot: "bg-orange-500", bg: "bg-orange-500/10", label: "Service Call" },
  crm: { Icon: Users, dot: "bg-purple-500", bg: "bg-purple-500/10", label: "CRM" },
  document: { Icon: FileText, dot: "bg-slate-400", bg: "bg-slate-500/10", label: "Document" },
  user: { Icon: UserCheck, dot: "bg-indigo-500", bg: "bg-indigo-500/10", label: "User" },
  leave: { Icon: CalendarOff, dot: "bg-teal-500", bg: "bg-teal-500/10", label: "Leave" },
};

const DEFAULT_META = { Icon: Bell, dot: "bg-sky-500", bg: "bg-sky-500/10", label: "Notification" };
const NOTIFICATION_SOURCE = "NotificationBell";

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  return `${Math.floor(hours / 24)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const syncUnreadCount = useCallback((nextUnread) => {
    setUnread(nextUnread);
    setCachedValue(CACHE_KEYS.notificationsUnread, nextUnread);
  }, []);

  const fetchNotifs = useCallback(async (signal) => {
    try {
      const response = await runTrackedRequest({
        path: "/notifications/",
        source: NOTIFICATION_SOURCE,
        signal,
        request: ({ signal: requestSignal }) => api.get("/notifications/", { signal: requestSignal }),
      });
      const items = Array.isArray(response.data) ? response.data : [];
      setNotifs(items);
      syncUnreadCount(items.filter((notif) => !notif.is_read).length);
    } catch (error) {
      if (!isAbortError(error)) {
        setNotifs([]);
      }
    }
  }, [syncUnreadCount]);

  const fetchUnreadCount = useCallback(async ({ force = false, signal } = {}) => {
    try {
      const { data } = await getOrFetchCached({
        key: CACHE_KEYS.notificationsUnread,
        ttlMs: CACHE_TTLS.notificationsUnread,
        force,
        source: NOTIFICATION_SOURCE,
        path: "/notifications/unread-count",
        signal,
        fetcher: async ({ signal: requestSignal }) => {
          const response = await api.get("/notifications/unread-count", { signal: requestSignal });
          return response.data.unread || 0;
        },
      });
      syncUnreadCount(data);
    } catch (error) {
      if (!isAbortError(error)) {
        syncUnreadCount(0);
      }
    }
  }, [syncUnreadCount]);

  useVisibilityPolling(() => {
    fetchUnreadCount();
  }, POLL_INTERVALS.notificationsUnread, { enabled: !open });

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const controller = new AbortController();
    fetchNotifs(controller.signal);

    const handler = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => {
      controller.abort();
      document.removeEventListener("mousedown", handler);
    };
  }, [fetchNotifs, open]);

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      await markOne(notif.id);
    }
    const path = getNotificationDestination(notif);
    navigate(path);
    setOpen(false);
  };

  const markOne = async (id) => {
    try {
      const response = await runTrackedRequest({
        path: `/notifications/${id}/read`,
        source: NOTIFICATION_SOURCE,
        request: () => api.patch(`/notifications/${id}/read`),
      });
      setNotifs((prev) => prev.map((notif) => (notif.id === id ? response.data : notif)));
      syncUnreadCount(Math.max(0, unread - 1));
    } catch {}
  };

  const markAll = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      await runTrackedRequest({
        path: "/notifications/read-all",
        source: NOTIFICATION_SOURCE,
        request: () => api.post("/notifications/read-all"),
      });
      setNotifs((prev) => prev.map((notif) => ({ ...notif, is_read: true })));
      syncUnreadCount(0);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const clearAll = async () => {
    try {
      await runTrackedRequest({
        path: "/notifications/clear-all",
        source: NOTIFICATION_SOURCE,
        request: () => api.delete("/notifications/clear-all"),
      });
      setNotifs([]);
      syncUnreadCount(0);
    } catch {}
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 transition hover:bg-slate-100 dark:hover:bg-slate-800"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white shadow">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-1.5rem)] max-w-sm overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl sm:w-96">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/80 px-4 py-3">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-100">Notifications</span>
              {unread > 0 && (
                <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-300">
                  {unread} new
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAll}
                  disabled={loading}
                  className="flex items-center gap-1 text-xs font-medium text-sky-300 transition hover:text-sky-200 disabled:opacity-50"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  {loading ? "Marking..." : "Mark all read"}
                </button>
              )}
              {notifs.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="flex items-center gap-1 text-xs font-medium text-slate-500 transition hover:text-rose-400"
                  title="Clear all notifications"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>

          <ul className="max-h-[70vh] divide-y divide-slate-900 overflow-y-auto sm:max-h-96">
            {notifs.length === 0 ? (
              <li className="py-12 text-center">
                <Bell className="mx-auto mb-3 h-10 w-10 text-slate-700" />
                <p className="text-sm font-medium text-slate-400">No notifications yet</p>
                <p className="mt-1 text-xs text-slate-600">Task, expense, and service updates will appear here.</p>
              </li>
            ) : (
              notifs.map((notif) => {
                const meta = TYPE_META[notif.notif_type] || DEFAULT_META;
                const { Icon } = meta;

                return (
                  <li
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`flex cursor-pointer gap-3 px-4 py-3.5 transition-colors ${
                      notif.is_read ? "hover:bg-slate-900/60" : "bg-sky-500/5 hover:bg-sky-500/10"
                    }`}
                  >
                    <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${meta.bg}`}>
                      <Icon className={`h-4 w-4 ${notif.is_read ? "text-slate-500" : meta.dot.replace("bg-", "text-")}`} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-tight ${notif.is_read ? "text-slate-300" : "font-semibold text-slate-100"}`}>
                          {cleanMsg(notif.title) || meta.label}
                        </p>
                        {!notif.is_read && <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${meta.dot}`} />}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-400">
                        {cleanMsg(notif.message)}
                      </p>
                      <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-600">
                        <Clock3 className="h-3 w-3" />
                        {timeAgo(notif.created_at)}
                      </p>
                    </div>
                  </li>
                );
              })
            )}
          </ul>

          {notifs.length > 0 && (
            <div className="border-t border-slate-800 bg-slate-950 px-4 py-2 text-center">
              <span className="text-xs text-slate-600">Showing last {notifs.length} notifications</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
